/**
 * Diligence Card
 * ==============
 * The company diligence run, and what fills a lead's Due Diligence tab.
 *
 * Two shapes, not five states. A run that finished and stored a PDF renders
 * the report itself, through `DiligenceReportViewer`. Every other case fills
 * the tab with one tall panel that says what happened and offers the action
 * that fits it.
 *
 * A lead holds one row per version, and the card reads all of them. `run` is
 * the highest version, and it alone decides which of the two shapes fills the
 * tab. The versions that hold a report fill the viewer's switcher, so an
 * operator can read a superseded report without leaving the tab.
 *
 * An earlier report never fills the tab. When the latest run failed, the
 * failure is the news, so the panel wins and any stored document — an earlier
 * version's, or this row's own — stays one click away on the Open previous
 * report link.
 *
 * The "Run steps" section carries its own progress header — a bar, a
 * percentage and the elapsed time — read off the same record its rows are, so
 * the two can never disagree. In the panel shape the section sits ABOVE the
 * panel, because during a run how far it has got is the news. In the report
 * shape it sits below the viewer, because a delivered report is.
 *
 * The card polls while a run is in flight and stops as soon as there is
 * nothing left to learn.
 *
 * A report URL is signed at the moment it is needed, never reused from an
 * earlier poll. A presigned URL lives one hour, and a card left open
 * overnight would otherwise hand the operator a dead link.
 *
 * The card shows no spend. What a run cost appears in the lead page's Total
 * Cost tile instead. The card reports what happened; the tile carries what it
 * cost. `onRunSettled` is how the tile learns a run has finished.
 *
 * `onStatusChange` is how the lead page's stepper learns the run's status.
 * The stepper's first step used to read `lead.research_status`, which is now
 * permanently `pending`. See the cycle
 * `diligence-only-lead-tab-and-branded-report`.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardBody, Button, Badge } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { getDiligenceStatus } from '../../api/diligence';
import RunDiligenceModal from './RunDiligenceModal';
import DiligenceReportViewer from './DiligenceReportViewer';

// Thirty seconds, not five. A run lasts about 27 minutes, and a five-second
// poll would make 324 requests to learn one fact.
const POLL_INTERVAL_MS = 30000;

// A client-side copy of the server's own rule. The server still writes
// `timed_out`; the client stops so a tab left open overnight does not make
// 2,880 requests.
const RUN_TIMEOUT_MS = 60 * 60 * 1000;

const IN_FLIGHT_STATUSES = ['queued', 'running'];

// The two statuses that mean a report was produced. Both render it: a
// gate-failed run's report is still the document, shown with a warning.
const REPORT_STATUSES = ['completed', 'completed_gate_failed'];

// Written by the backend into `error`. Repeated here only as the fallback for
// a row that somehow carries no reason, so the card never shows a bare status.
const TIMED_OUT_TEXT =
  'The diligence service did not report back within one hour. The run may ' +
  'still be going. Press Try again to start a new one.';

// What the report covers, for a lead that has never had a run. Plain nouns,
// so an operator deciding whether to spend USD 8 to 12 can see what arrives.
const REPORT_SUBJECTS = [
  'Ownership and group structure',
  'Regulators and licences held',
  'Published financial statements',
  'Court and enforcement records',
  'Named directors and shareholders',
  'Press and public record',
];

const isInFlight = (status) => IN_FLIGHT_STATUSES.includes(status);

// A version that finished and stored its document. These are the switcher's
// options, and the newest of them is what can fill the tab. Read on a version
// row, so it answers for `run` and for any entry of `versions` alike.
const isReportVersion = (version) =>
  REPORT_STATUSES.includes(version?.status) && Boolean(version?.pdf_key);

// The looser question: is there a document to open at all. A run can store its
// report and then settle as failed — the callback writes the key before it
// reads the status — so a `failed` row can hold a readable document. Such a
// document is offered on the previous-report link rather than dropped.
const holdsDocument = (version) => Boolean(version?.pdf_key);

// The freshly signed link for one version, found by its number. Falls back to
// the newest version holding a document.
const readReportUrl = (list, wanted) => {
  const readable = list.filter(holdsDocument);
  const match = readable.find((v) => v.version === wanted) || readable[0] || null;
  return match?.pdf_url || null;
};

const startedLongAgo = (startedAt) => {
  if (!startedAt) return false;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return false;
  return Date.now() - started > RUN_TIMEOUT_MS;
};

const formatElapsed = (startedAt) => {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - started) / 60000));
  if (minutes < 1) return 'Started less than a minute ago';
  if (minutes === 1) return 'Running for 1 minute';
  return `Running for ${minutes} minutes`;
};

const formatMoment = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

// ---------------------------------------------------------------------------
// Run steps
// ---------------------------------------------------------------------------
//
// A run is three steps: research, then collation, then synthesis. The row's
// `logs` column holds what each of them did. The service writes it; the card
// only reads it, and decides nothing about a status here.
//
// Every run stored before this column existed carries a null `logs`, and such
// a run renders no section at all rather than three empty rows.

// The three steps in order, with the name of each step's inner map of legs.
// This mirrors the service's own `STEP_LEGS_KEY`. The order is fixed here
// rather than read from the object, because an object's key order is not a
// promise and a step out of place reads as a rendering fault.
// Each step also carries one line saying what it produces, and an icon. An
// operator meeting the section for the first time should not have to infer
// what "Collation" is from the legs underneath it.
const RUN_STEPS = [
  {
    key: 'step_1',
    number: 1,
    title: 'Research',
    legsKey: 'researches',
    blurb: 'Each provider researches the company and returns one pack',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    key: 'step_2',
    number: 2,
    title: 'Collation',
    legsKey: 'collation',
    blurb: 'The usable packs are merged into one set of sources',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7l8-4 8 4-8 4-8-4zm0 5l8 4 8-4M4 17l8 4 8-4" />
      </svg>
    ),
  },
  {
    key: 'step_3',
    number: 3,
    title: 'Synthesis',
    legsKey: 'synthesis',
    blurb: 'The report is written from the merged sources, then checked',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

// One status word, one mark and one plain label. `blocked` and `skipped` both
// read "not run": a step that never ran cost nothing, and calling it failed
// would send an operator looking for a bill that does not exist. The word
// `blocked` is a term of art, so the plain words lead and the gate finding
// beside the row says what stopped it.
// `tone` colours the word. `pill` is the chip the word sits in, and `panel`
// tints the step's own card, so an operator scanning the section sees the
// outcome before reading anything.
const STEP_STATUS_STYLE = {
  completed: {
    label: 'completed',
    tone: 'text-green-700 dark:text-green-400',
    pill: 'bg-green-50 dark:bg-green-900/25',
    panel: 'bg-green-50/40 dark:bg-green-900/10',
    icon: 'text-green-600 dark:text-green-400',
  },
  failed: {
    label: 'failed',
    tone: 'text-red-700 dark:text-red-400',
    pill: 'bg-red-50 dark:bg-red-900/25',
    panel: 'bg-red-50/40 dark:bg-red-900/10',
    icon: 'text-red-600 dark:text-red-400',
  },
  running: {
    label: 'running',
    tone: 'text-primary dark:text-primary-300',
    pill: 'bg-primary-50 dark:bg-primary-900/25',
    panel: 'bg-primary-50/50 dark:bg-primary-900/10',
    icon: 'text-primary dark:text-primary-300',
  },
  pending: {
    label: 'not started',
    tone: 'text-gray-500 dark:text-[#6E7278]',
    pill: 'bg-gray-100 dark:bg-[#22252A]',
    panel: 'bg-gray-50/60 dark:bg-[#1A1D21]',
    icon: 'text-gray-400 dark:text-[#6E7278]',
  },
  skipped: {
    label: 'not run',
    tone: 'text-gray-500 dark:text-[#6E7278]',
    pill: 'bg-gray-100 dark:bg-[#22252A]',
    panel: 'bg-gray-50/60 dark:bg-[#1A1D21]',
    icon: 'text-gray-400 dark:text-[#6E7278]',
  },
  blocked: {
    label: 'not run',
    tone: 'text-amber-700 dark:text-amber-400',
    pill: 'bg-amber-50 dark:bg-amber-900/25',
    panel: 'bg-amber-50/40 dark:bg-amber-900/10',
    icon: 'text-amber-600 dark:text-amber-400',
  },
};

const UNKNOWN_STATUS_STYLE = {
  label: 'unknown',
  tone: 'text-gray-500 dark:text-[#6E7278]',
  pill: 'bg-gray-100 dark:bg-[#22252A]',
  panel: 'bg-gray-50/60 dark:bg-[#1A1D21]',
  icon: 'text-gray-400 dark:text-[#6E7278]',
};

// A word this build does not know is shown as itself rather than dropped. A
// later service can add one, and an operator reading a card should see it.
const statusStyle = (status) =>
  STEP_STATUS_STYLE[status] || { ...UNKNOWN_STATUS_STYLE, label: status || 'unknown' };

/**
 * The right-hand mark on a step row.
 *
 * Drawn, not written. Every status word already appears once in the chip
 * beside the step's title, and a second copy here would say the same thing
 * twice to a reader and twice to a test counting them. So the mark carries no
 * text: it is `aria-hidden`, and the chip is what a screen reader reads.
 */
const StepStatusMark = ({ status }) => {
  if (status === 'completed') {
    return (
      <span
        aria-hidden="true"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500"
      >
        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" clipRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
        </svg>
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span
        aria-hidden="true"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500"
      >
        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" clipRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
        </svg>
      </span>
    );
  }

  if (status === 'running') {
    return (
      <span
        aria-hidden="true"
        className="flex h-5 w-5 shrink-0 items-center justify-center"
      >
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
      </span>
    );
  }

  // `blocked` and `skipped` both mean the step never ran, so both get the
  // same drawn dash. Amber against grey is the only difference, and it comes
  // from the style table.
  if (status === 'blocked' || status === 'skipped') {
    const tone = status === 'blocked' ? 'text-amber-500' : 'text-gray-400 dark:text-[#6E7278]';
    return (
      <span aria-hidden="true" className={`flex h-5 w-5 shrink-0 items-center justify-center ${tone}`}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeWidth={2} d="M6 12h12" />
        </svg>
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className="h-5 w-5 shrink-0 rounded-full border-2 border-dashed border-gray-300 dark:border-[#3A3E44]"
    />
  );
};

// ---------------------------------------------------------------------------
// Run progress
// ---------------------------------------------------------------------------
//
// The bar and the percentage are read off the same record the rows are, so
// the two can never disagree. The card computes no status — it only measures
// how much of the pipeline the service has reported as carried out.

// A leg that will not change again.
const LEG_SETTLED = ['completed', 'failed', 'skipped', 'blocked'];

// How much of one step has been carried out, from 0 to 1.
//
// A failed step counts a half: it ran and spent money, and it did not
// deliver. A blocked step counts nothing, because it never ran at all. That
// keeps the bar honest — a run stopped by the collation gate reads about a
// third, not a full bar in red.
const stepShare = (step, legsKey) => {
  const status = step?.status;
  if (status === 'completed' || status === 'skipped') return 1;
  if (status === 'failed') return 0.5;
  if (status === 'running') {
    const legs = legsOf(step, legsKey);
    if (legs.length === 0) return 0.15;
    const settled = legs.filter((leg) => LEG_SETTLED.includes(leg.status)).length;
    // Never a full step while the step itself still reads `running`.
    return Math.min(0.9, Math.max(0.15, settled / legs.length));
  }
  return 0;
};

// One reading of the whole run: the bar's width, its colour, and the sentence
// under it. `live` is the card's own answer to "is this row still in flight",
// passed in rather than guessed from the record.
const readRunProgress = (logs, live) => {
  const steps = RUN_STEPS.map(({ key, legsKey, number, title }) => ({
    number,
    title,
    status: logs?.[key]?.status || 'pending',
    share: stepShare(logs?.[key], legsKey),
  }));

  const percent = Math.round((steps.reduce((sum, s) => sum + s.share, 0) / steps.length) * 100);
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const firstFailed = steps.find((s) => s.status === 'failed') || null;
  const firstBlocked = steps.find((s) => s.status === 'blocked') || null;

  let outcome;
  let caption;

  if (live) {
    outcome = 'running';
    caption = `${completedCount} of ${steps.length} steps completed`;
  } else if (firstFailed) {
    outcome = 'failed';
    caption = `Step ${firstFailed.number} ${firstFailed.title} failed`;
  } else if (firstBlocked) {
    outcome = 'stopped';
    caption = `Stopped at step ${firstBlocked.number} ${firstBlocked.title}`;
  } else if (completedCount + steps.filter((s) => s.status === 'skipped').length === steps.length) {
    outcome = 'completed';
    caption = `All ${steps.length} steps completed`;
  } else {
    outcome = 'partial';
    caption = `${completedCount} of ${steps.length} steps completed`;
  }

  return { percent, caption, outcome };
};

// The bar's fill, by outcome. The live gradient and its shimmer are the same
// pair the proposal generation screen uses, so the two screens read as one
// product.
const PROGRESS_FILL = {
  running: 'bg-gradient-to-r from-primary via-accent to-primary-400',
  completed: 'bg-gradient-to-r from-primary to-accent',
  failed: 'bg-red-500',
  stopped: 'bg-amber-500',
  partial: 'bg-gray-400 dark:bg-[#4A4E54]',
};

const PROGRESS_PERCENT_TONE = {
  running: 'text-primary dark:text-primary-300',
  completed: 'text-primary dark:text-primary-300',
  failed: 'text-red-600 dark:text-red-400',
  stopped: 'text-amber-600 dark:text-amber-400',
  partial: 'text-gray-600 dark:text-[#A0A4A8]',
};

// A step record the card can render. Anything else — a null, a string, a shape
// with no steps in it — renders no section.
const hasStepLogs = (logs) =>
  Boolean(logs) &&
  typeof logs === 'object' &&
  !Array.isArray(logs) &&
  RUN_STEPS.some(({ key }) => logs[key] && typeof logs[key] === 'object');

const formatClock = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// How long a step took, from its own two timestamps. Null while it is still
// running, because a duration with no end is not a duration.
const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return null;
  const from = new Date(startedAt).getTime();
  const to = new Date(endedAt).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  const minutes = Math.round((to - from) / 60000);
  if (minutes < 1) return 'under a minute';
  return `${minutes} min`;
};

const formatCost = (value) => {
  const amount = Number(value);
  if (value === null || value === undefined || Number.isNaN(amount)) return null;
  return `USD ${amount.toFixed(2)}`;
};

// The legs of one step, in the order the service wrote them.
const legsOf = (step, legsKey) => {
  const legs = step?.[legsKey];
  if (!legs || typeof legs !== 'object' || Array.isArray(legs)) return [];
  return Object.entries(legs).map(([name, leg]) => ({ name, ...(leg || {}) }));
};

/** One leg of a step: a researcher, or the single job of collation or synthesis. */
const StepLeg = ({ leg }) => {
  const style = statusStyle(leg.status);
  const response = leg.response || {};
  const cost = formatCost(response.cost_usd);
  const subJobs = leg.sub_jobs || null;

  return (
    <li className={`rounded-lg px-3 py-2.5 ${style.panel}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium text-gray-800 dark:text-[#E4E6E8]">
              {leg.name}
            </span>
            <span className={`shrink-0 text-xs ${style.tone}`}>{style.label}</span>
          </div>

          {/* The two figures a leg is read for, on their own line so they line
              up down a column of legs rather than trailing the name. */}
          {(cost || response.coverage) && (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-xs tabular-nums text-gray-500 dark:text-[#6E7278]">
              {cost && <span>{cost}</span>}
              {response.coverage && <span>{response.coverage} sections</span>}
            </div>
          )}
        </div>
        <StepStatusMark status={leg.status} />
      </div>

      {/* The `deep` profile splits one provider into nine specialist jobs.
          They are counted rather than listed, so the row stays readable. */}
      {subJobs && Number(subJobs.total) > 0 && (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-[#6E7278]">
          {subJobs.completed} of {subJobs.total} specialist jobs completed
          {Number(subJobs.failed) > 0 ? `, ${subJobs.failed} failed` : ''}
        </p>
      )}

      {/* In full. The engine caps it at 500 characters, and it is the reason
          the operator opened the card. */}
      {response.error && (
        <p className="mt-1.5 whitespace-pre-wrap break-words rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {response.error}
        </p>
      )}
    </li>
  );
};

/** The gate recorded on a step, and its findings when it failed. */
const StepGate = ({ gate }) => {
  if (!gate || !gate.name) return null;
  const failed = gate.result !== 'passed';
  const findings = Array.isArray(gate.findings) ? gate.findings.filter(Boolean) : [];

  // A passed gate is one quiet line. A failed one is the reason the step
  // stopped, so it gets a tinted box and its findings are listed under it.
  return (
    <div
      className={
        failed
          ? 'mt-2 rounded-md bg-amber-50 px-2.5 py-2 text-xs dark:bg-amber-900/20'
          : 'mt-2 text-xs'
      }
    >
      <div className="flex items-baseline gap-1.5">
        <span className="text-gray-500 dark:text-[#6E7278]">Gate: {gate.name}</span>
        <span className={failed ? 'font-medium text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}>
          {failed ? 'failed' : 'passed'}
        </span>
      </div>
      {failed && findings.length > 0 && (
        <ul className="mt-1 space-y-1">
          {findings.map((finding, index) => (
            <li
              key={`${finding}-${index}`}
              className="whitespace-pre-wrap break-words text-gray-700 dark:text-[#A0A4A8]"
            >
              {String(finding)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * One numbered step, with its legs.
 *
 * The legs open by default when any of them failed, and stay closed when the
 * step completed with nothing to read. A click overrides that for as long as
 * the card is open: `override` is null until the operator presses the row, so
 * a poll that arrives with a newly failed leg still opens itself.
 */
const StepRow = ({ step, number, title, legsKey, blurb, icon }) => {
  const [override, setOverride] = useState(null);

  // A step the record does not describe is still drawn, and reads "not
  // started". The alternative is a missing row, which reads as a rendering
  // fault. A step that IS described but carries a word this build does not
  // know keeps that word, through `statusStyle`.
  const status = step?.status || 'pending';
  const style = statusStyle(status);
  const legs = legsOf(step, legsKey);
  const failedLeg = legs.some((leg) => leg.status === 'failed');
  const gate = step?.gate || null;
  const gateFailed = Boolean(gate && gate.result !== 'passed');

  const started = formatClock(step?.started_at);
  const ended = formatClock(step?.ended_at);
  const duration = formatDuration(step?.started_at, step?.ended_at);

  // Nothing to hide behind a toggle when the step holds no legs. A pending
  // step and a skipped one are both in that case.
  const canToggle = legs.length > 0;
  const openByDefault = failedLeg || gateFailed;
  const expanded = canToggle && (override === null ? openByDefault : override);

  // Three fixed columns: the step's icon, its name and blurb, then its timing
  // and mark. The timing column has a set width and `tabular-nums`, so the
  // clock ranges and the durations line up down the three rows instead of
  // trailing names of different lengths. That ragged trail is what the old
  // single flex row produced.
  const heading = (
    <div className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-x-3">
      <span className={`flex h-7 w-7 items-center justify-center ${style.icon}`} aria-hidden="true">
        {icon}
      </span>

      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="w-[7.5rem] shrink-0 truncate text-sm font-semibold text-gray-900 dark:text-white">
            {number}. {title}
          </span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${style.pill} ${style.tone}`}
          >
            {style.label}
          </span>
        </div>
        {blurb && (
          <p className="mt-1 truncate text-xs text-gray-500 dark:text-[#6E7278]">{blurb}</p>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Hidden on the narrowest screens rather than wrapped under the name,
            because a wrapped clock range is exactly the misalignment this
            column exists to remove. */}
        <div className="hidden w-[9rem] text-right text-xs leading-tight tabular-nums text-gray-500 sm:block dark:text-[#6E7278]">
          {started && (
            <div>
              {started}
              {ended ? ` \u2192 ${ended}` : ''}
            </div>
          )}
          {duration && <div className="mt-0.5">{duration}</div>}
        </div>

        <StepStatusMark status={status} />

        {canToggle && (
          <svg
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 dark:text-[#6E7278] ${
              expanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>
    </div>
  );

  return (
    <li
      className={`rounded-xl border border-gray-200 px-3 py-3 transition-colors dark:border-[#2A2D31] ${style.panel}`}
    >
      {canToggle ? (
        <button
          type="button"
          onClick={() => setOverride(!expanded)}
          aria-expanded={expanded}
          className="w-full rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {heading}
        </button>
      ) : (
        heading
      )}

      {/* Everything below the heading is indented to the text column on wide
          screens, so the step's own detail sits under its name and not under
          its icon. */}
      <div className="sm:pl-[2.5rem]">
        {/* One sentence the counts cannot give. Written by the service, and the
            only place a `skipped` or `blocked` step says why. */}
        {step?.note && (
          <p className="mt-2 text-xs text-gray-600 dark:text-[#A0A4A8]">{step.note}</p>
        )}

        <StepGate gate={gate} />

        {/* `items-start` so a leg carrying a long error does not stretch the
            leg beside it to match its height. */}
        {expanded && (
          <ul
            className={`mt-3 grid items-start gap-2 ${legs.length > 1 ? 'sm:grid-cols-2' : ''}`}
          >
            {legs.map((leg) => (
              <StepLeg key={leg.name} leg={leg} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

/**
 * The Run steps section.
 *
 * All three steps are always listed. A missing row reads as a rendering fault
 * rather than as a step that has not started, so a step the record does not
 * describe is still drawn, as "not started".
 */
// The run's own span, for the header. While the row is in flight it counts
// from the row's start to now. Once the row has settled it is the record's
// first start to its last end — the run's length, not the tab's age.
//
// It moves when a poll arrives, every thirty seconds. Nothing here starts a
// clock of its own, because the section's whole rule is that it reads the
// record and adds nothing.
const runSpan = (logs, startedAt, live) => {
  if (live) {
    if (!startedAt) return null;
    const from = new Date(startedAt).getTime();
    if (Number.isNaN(from)) return null;
    const minutes = Math.max(0, Math.round((Date.now() - from) / 60000));
    return minutes < 1 ? 'under a minute' : `${minutes} min`;
  }
  const starts = RUN_STEPS.map(({ key }) => logs?.[key]?.started_at).filter(Boolean);
  const ends = RUN_STEPS.map(({ key }) => logs?.[key]?.ended_at).filter(Boolean);
  if (starts.length === 0 || ends.length === 0) return null;
  return formatDuration(starts[0], ends[ends.length - 1]);
};

const DiligenceRunSteps = ({ logs, live = false, startedAt = null }) => {
  if (!hasStepLogs(logs)) return null;

  const { percent, caption, outcome } = readRunProgress(logs, live);
  const span = runSpan(logs, startedAt, live);

  return (
    <Card className="overflow-hidden">
      {/* The header strip. It carries the one reading an operator wants
          first — how far the run has got — so it sits above the rows rather
          than under them. */}
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-5 dark:border-[#3A3C3E] dark:bg-[#1E2124]">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-[#6E7278]">
            Run steps
          </h4>
          <div className="flex items-center gap-3">
            {span && (
              <span className="text-xs tabular-nums text-gray-500 dark:text-[#6E7278]">
                {live ? `Elapsed ${span}` : `${span} total`}
              </span>
            )}
            {live && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                Live
              </span>
            )}
          </div>
        </div>

        <div className="relative mt-2.5 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-[#2A2D31]">
          {/* The width is the one thing Tailwind cannot carry, because the
              value is computed per render. */}
          <div
            className={`relative h-full rounded-full transition-all duration-700 ease-out ${PROGRESS_FILL[outcome]}`}
            style={{ width: `${percent}%` }}
          >
            {live && (
              <span className="absolute inset-0 overflow-hidden rounded-full">
                <span className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </span>
            )}
          </div>
        </div>

        <div className="mt-1.5 flex items-baseline justify-between gap-3">
          <span className="text-xs text-gray-600 dark:text-[#A0A4A8]">{caption}</span>
          <span className={`text-sm font-semibold tabular-nums ${PROGRESS_PERCENT_TONE[outcome]}`}>
            {percent}%
          </span>
        </div>
      </div>

      {/* A plain div rather than `CardBody`, whose own `p-6` would fight the
          padding this section wants. */}
      <div className="p-4 sm:p-5">
        <ul className="space-y-2.5">
          {RUN_STEPS.map(({ key, number, title, legsKey, blurb, icon }) => (
            <StepRow
              key={key}
              step={logs[key] || null}
              number={number}
              title={title}
              legsKey={legsKey}
              blurb={blurb}
              icon={icon}
            />
          ))}
        </ul>
      </div>
    </Card>
  );
};

const DiligenceCard = ({
  leadId,
  companyName,
  leadWebsite,
  onRunSettled,
  onStatusChange,
}) => {
  const [run, setRun] = useState(null);
  const [versions, setVersions] = useState([]);
  // The version the operator is reading, held as a version NUMBER rather than
  // an index or a row object. Every read replaces every row object, and an
  // index moves when a new version is inserted. A number survives both, so a
  // re-sign leaves the operator on the version they were reading.
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [opening, setOpening] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { can } = usePermissions();
  const toast = useToast();

  // The permission gate matches `require_org_permission("leads:update")` on
  // the backend route, so the two agree.
  const canRun = can.updateLead();

  // The poll depends on these two values, not on the whole row. A polled
  // response that changes nothing else must not recreate the interval, or the
  // thirty-second clock restarts on every answer.
  const status = run?.status || null;
  const startedAt = run?.started_at || null;

  // The status the last render saw, so a poll answer can be compared with it.
  // A ref, not state: changing it must not cause a render.
  const previousStatusRef = useRef(null);

  // Two things the page learns from one comparison.
  //
  // `onRunSettled` fires only on the move OUT of flight. The page fetches
  // analytics only on load, so without it the Total Cost tile would keep
  // reading the figure from before the run finished. A first read that
  // already shows a terminal status is not a transition — the page's own load
  // already counted that run.
  //
  // `onStatusChange` fires on ANY change, so the stepper follows the run from
  // queued through to completed. It is guarded by the same comparison, so a
  // poll that returns the same status again fires nothing. That matters: the
  // card polls every thirty seconds for about 27 minutes.
  useEffect(() => {
    const previous = previousStatusRef.current;
    if (status === previous) return;
    previousStatusRef.current = status;

    if (onStatusChange) onStatusChange(status);
    if (isInFlight(previous) && status && !isInFlight(status)) {
      if (onRunSettled) onRunSettled();
    }
  }, [status, onRunSettled, onStatusChange]);

  const fetchStatus = useCallback(async () => {
    const response = await getDiligenceStatus(leadId);
    const payload = response?.data || {};
    const latest = payload.run || null;
    // A response carrying no `versions` is read as a lead with one version.
    // `run` is the highest version, so a list holding only it is a correct
    // history of length one, and the tab behaves as it did before versioning.
    const history =
      Array.isArray(payload.versions) && payload.versions.length > 0
        ? payload.versions
        : (latest ? [latest] : []);
    return { run: latest, versions: history, enabled: Boolean(payload.diligence_enabled) };
  }, [leadId]);

  // One read, applied to state. Every re-sign goes through here, because every
  // `pdf_url` — on `run` and on each version — is signed at read time.
  const applyResult = useCallback((result) => {
    setRun(result.run);
    setVersions(result.versions);
    setEnabled(result.enabled);
  }, []);

  const refreshRun = useCallback(async () => {
    const result = await fetchStatus();
    applyResult(result);
    return result;
  }, [fetchStatus, applyResult]);

  useEffect(() => {
    let isMounted = true;
    fetchStatus()
      .then((result) => {
        if (!isMounted) return;
        applyResult(result);
      })
      .catch(() => {
        // A failed read is not an operator error. The card stays hidden rather
        // than reporting a problem the operator cannot act on.
      })
      .finally(() => {
        if (isMounted) setLoaded(true);
      });
    return () => {
      isMounted = false;
    };
  }, [fetchStatus, applyResult]);

  // Polling. Runs only while the row is in flight and inside the timeout
  // window.
  useEffect(() => {
    if (!enabled) return undefined;
    // Nothing left to learn once the status is terminal.
    if (!isInFlight(status)) return undefined;
    // The backend has already given up on this run.
    if (startedLongAgo(startedAt)) return undefined;

    let isMounted = true;
    let inFlightRequest = false;
    let intervalId = setInterval(async () => {
      if (!isMounted || inFlightRequest) return;
      inFlightRequest = true;
      try {
        const result = await fetchStatus();
        if (!isMounted) return;
        applyResult(result);
      } catch {
        // Keep polling. One failed read does not mean the run is lost.
      } finally {
        inFlightRequest = false;
      }
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [enabled, status, startedAt, fetchStatus, applyResult]);

  // Every version holding a readable report, newest first. The status route
  // returns `versions` newest first, so the switcher's order and the default
  // selection both come straight from it.
  const reportVersions = useMemo(() => versions.filter(isReportVersion), [versions]);

  // Hold the selection on a version number the lead still has. A re-sign
  // returns the same numbers, so the operator stays on the version they were
  // reading. A first read, or a number that has gone, takes the newest report.
  useEffect(() => {
    setSelectedVersion((previous) => {
      if (reportVersions.length === 0) return null;
      const stillThere = reportVersions.some((v) => v.version === previous);
      return stillThere ? previous : reportVersions[0].version;
    });
  }, [reportVersions]);

  const selectedRun =
    reportVersions.find((v) => v.version === selectedVersion) || reportVersions[0] || null;

  const handleStarted = (started) => {
    // The start route answers with the row it just wrote, serialised the same
    // way the status route serialises it. The card shows the spinner, and the
    // run steps section, without waiting for the first poll. The steps read
    // `logs`, which the row carries from the moment it opens: three steps,
    // all pending. A body that omitted it left the operator on a bare panel
    // for thirty seconds.
    //
    // The row REPLACES the previous one rather than merging into it. A re-run
    // after a completed run writes a new version, and merging would leave the
    // new version carrying the old one's `pdf_key` and signed link. The
    // earlier report is not lost by the replacement: it keeps its own entry in
    // `versions`, which is where the report now comes from.
    if (started) {
      setRun(started);
      setVersions((prev) => [
        started,
        ...prev.filter((v) => v.version !== started.version),
      ]);
    }
    toast.success('Diligence run started.');
  };

  const handleOpenReport = async (wanted) => {
    // The tab is opened synchronously, inside the click, because a browser
    // blocks a window.open that happens after an await. It is then navigated
    // to the URL from the fresh read. `opener` is cleared so the report page
    // cannot reach back into the portal.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;

    setOpening(true);
    try {
      const result = await refreshRun();
      // Always the URL this read returned, and always for the version the
      // caller named — the switcher's selection, or the version behind the
      // previous-report link. A URL from an earlier poll is already up to an
      // hour old, and a presigned URL lives exactly an hour.
      const url = readReportUrl(result.versions, wanted);
      if (url) {
        if (tab) {
          tab.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } else {
        if (tab) tab.close();
        toast.error('The report link could not be signed. Try again.');
      }
    } catch {
      if (tab) tab.close();
      toast.error('The report could not be opened. Try again.');
    } finally {
      setOpening(false);
    }
  };

  // The 50-minute timer's callback. Silent on purpose: it must not flash a
  // spinner over a report somebody is reading. Stable across renders, so the
  // viewer's interval is not restarted on every parent render.
  const handleResign = useCallback(() => {
    refreshRun().catch(() => {
      // The frame keeps the URL it has. Reload is still offered.
    });
  }, [refreshRun]);

  // Pressed by hand, when a laptop slept through the timer and the frame is
  // showing a dead URL.
  const handleReload = async () => {
    setReloading(true);
    try {
      await refreshRun();
    } catch {
      toast.error('The report could not be reloaded. Try again.');
    } finally {
      setReloading(false);
    }
  };

  // The whole card is hidden when the feature is switched off on this
  // deployment, and until the first read has answered.
  if (!loaded || !enabled) return null;

  const runButton = (label) =>
    canRun && (
      <Button size="sm" onClick={() => setShowModal(true)}>
        {label}
      </Button>
    );

  const modal = showModal && (
    <RunDiligenceModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      leadId={leadId}
      companyName={companyName}
      leadWebsite={leadWebsite}
      previousRun={run}
      onStarted={handleStarted}
    />
  );

  // The LATEST version's own report is what fills the tab with the document.
  // An earlier report does not: when the latest run failed, the failure is the
  // news, so the panel below wins and the report stays one click away.
  const latestHasReport = isReportVersion(run);

  if (latestHasReport && selectedRun) {
    return (
      <>
        <DiligenceReportViewer
          run={selectedRun}
          versions={reportVersions}
          selectedVersion={selectedRun.version}
          onSelectVersion={setSelectedVersion}
          reportUrl={selectedRun.pdf_url || null}
          onResign={handleResign}
          onReload={handleReload}
          onOpenInNewTab={() => handleOpenReport(selectedRun.version)}
          reloading={reloading}
          opening={opening}
          runAgainButton={runButton('Run again')}
        />
        {/* Kept after the run settles, so an operator reading a delivered
            report can still see what each step did and what it cost. It is the
            SELECTED version's record, matching the document above it.

            Below the viewer, and deliberately the other way round from the
            panel shape. A delivered report is what the operator came for, and
            the steps behind it are the supporting read. `live` is false here
            by construction: this shape is only reached by a version that
            finished and stored a document. */}
        <div className="mt-6">
          <DiligenceRunSteps logs={selectedRun.logs} />
        </div>
        {modal}
      </>
    );
  }

  // Everything else is one tall panel.
  //
  // The previous-report link replaces a status list read off `run`. A report
  // now lives on its own version row, so the latest row's status can no longer
  // stand in for the question "is a document reachable".
  //
  // `versions` is newest first, so this is the newest document the lead has.
  // It can be an earlier version's report, or this same row's own, when a run
  // stored a document and then settled as failed.
  const previousReport = versions.find(holdsDocument) || null;
  const previousReportMoment = formatMoment(previousReport?.pdf_generated_at);
  const showPreviousReport = previousReport !== null;

  const previousReportLink = showPreviousReport && (
    <button
      type="button"
      onClick={() => handleOpenReport(previousReport.version)}
      disabled={opening}
      className="mt-4 text-sm text-primary underline disabled:opacity-50"
    >
      {opening ? 'Opening previous report...' : 'Open previous report'}
      {previousReportMoment ? (
        <span className="ml-1 text-xs text-gray-500 dark:text-[#6E7278]">
          (produced {previousReportMoment})
        </span>
      ) : null}
    </button>
  );

  let hero;

  if (!run) {
    // State 1 — no row has ever existed for this lead.
    hero = {
      badge: null,
      icon: (
        <svg className="h-8 w-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      iconWrap: 'bg-primary/10',
      heading: 'No diligence report yet',
      lines: [
        'A researched report on this company: who owns it, how it is regulated, and what the public record says.',
        'It takes about 27 minutes and costs USD 8 to 12.',
      ],
      subjects: REPORT_SUBJECTS,
      action: runButton('Run Due Diligence'),
    };
  } else if (isInFlight(status)) {
    // State 2 — queued or running.
    hero = {
      badge: null,
      icon: <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />,
      iconWrap: '',
      heading: 'Diligence run in progress',
      lines: [
        `${formatElapsed(run.started_at)}. This usually takes about 27 minutes.`,
        'You may leave this page. The run continues without it, and the report is here when you come back.',
      ],
      subjects: null,
      action: null,
    };
  } else if (REPORT_STATUSES.includes(status)) {
    // State 3 — the run finished, but nothing was stored to read. Stated
    // plainly rather than shown as a completed run with no document.
    hero = {
      badge: <Badge variant="warning">No report stored</Badge>,
      icon: (
        <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-2.99l-6.93-12a2 2 0 00-3.48 0l-6.93 12A2 2 0 005.07 19z" />
        </svg>
      ),
      iconWrap: 'bg-amber-100 dark:bg-amber-900/30',
      heading: 'The run finished, but no report was stored',
      lines: [
        run.error ||
          'The diligence run reached the end without storing a report. Nothing is available to read.',
        'Running again produces a new report. It takes about 27 minutes and costs USD 8 to 12.',
      ],
      subjects: null,
      action: runButton('Run again'),
    };
  } else if (status === 'timed_out') {
    // State 4 — no answer. NOT described as failed: the run may still be
    // going, and the portal does not know.
    hero = {
      badge: <Badge variant="error">No answer</Badge>,
      icon: (
        <svg className="h-8 w-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      iconWrap: 'bg-gray-100 dark:bg-gray-800',
      heading: 'The diligence service went quiet',
      lines: [run.error || TIMED_OUT_TEXT],
      subjects: null,
      action: runButton('Try again'),
    };
  } else {
    // State 5 — the run failed.
    hero = {
      badge: <Badge variant="error">Failed</Badge>,
      icon: (
        <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      iconWrap: 'bg-red-100 dark:bg-red-900/30',
      heading: 'The diligence run failed',
      lines: [run.error || 'The diligence run failed.'],
      subjects: null,
      action: runButton('Try again'),
    };
  }

  // Whether the steps section will render above the panel. When it does, the
  // panel is no longer the first thing on the tab, so it stops reserving the
  // height of one.
  const stepsAbove = Boolean(run) && hasStepLogs(run.logs);

  // The in-flight panel's spinner is dropped once the steps section is above
  // it, because the bar up there is already turning and saying "Live". Two
  // spinners reporting one run read as two things happening. The panel keeps
  // its words, which are what it is for: how long this takes, and that the
  // operator may leave.
  const hideHeroIcon = stepsAbove && isInFlight(status);

  return (
    <>
      {/* Above the panel, not below it.

          The panel says what happened to the run as a whole; this says how
          far it has got and what each step did. During a run the second is
          the news, and an operator should not have to scroll past a tall
          centred panel to reach it. Once a report exists the order flips —
          see the report shape above, where the document leads and this
          section follows it. */}
      {run && (
        <div className="mb-6">
          <DiligenceRunSteps
            logs={run.logs}
            live={isInFlight(status)}
            startedAt={run.started_at}
          />
        </div>
      )}

      <Card>
        <CardBody>
          <div
            className={`mx-auto flex max-w-2xl flex-col items-center text-center ${
              stepsAbove ? 'py-8' : 'py-16'
            }`}
          >
            {hideHeroIcon ? null : hero.iconWrap ? (
              <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${hero.iconWrap}`}>
                {hero.icon}
              </div>
            ) : (
              <div className="mb-4">{hero.icon}</div>
            )}

            {hero.badge}

            <h3 className="mt-2 text-xl font-sans font-semibold text-gray-900 dark:text-white">
              {hero.heading}
            </h3>

            {hero.lines.map((line) => (
              <p key={line} className="mt-2 text-gray-600 dark:text-[#A0A4A8]">
                {line}
              </p>
            ))}

            {hero.subjects && (
              <div className="mt-6 w-full max-w-md text-left">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-[#6E7278]">
                  What the report covers
                </p>
                <ul className="mt-2 space-y-1">
                  {hero.subjects.map((subject) => (
                    <li key={subject} className="flex items-start gap-2 text-sm text-gray-700 dark:text-[#A0A4A8]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                      {subject}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hero.action && <div className="mt-8">{hero.action}</div>}

            {previousReportLink}
          </div>
        </CardBody>
      </Card>

      {modal}
    </>
  );
};

export default DiligenceCard;
